import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

import { RouteState } from '../types/surveillanceRoute';
import { Role } from '../types/auth';
import { useAuth } from './useAuth';
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';
import {
  appendGpsPoints,
  getLocalTrack,
  LocalGpsPoint,
} from '../offline/gpsTrackStore';
import { mergeStoredLiveTrack } from '../utils/mergeStoredLiveTrack';
import { haversineMeters } from '../utils/geo';
import { permissionApiDecides } from '../utils/webPermissions';

import {
  getPositionSource,
  drainNativeGpsBuffer,
  isNativeApp,
} from '../utils/positionSource';

/**
 * Filtro de movimiento (2026-07-20, reporte del usuario probando en casa):
 * `watchPosition` emite lecturas continuamente aunque el dispositivo esté
 * QUIETO — el jitter llenaba Mongo de puntos basura y disparaba
 * AddGpsPointsBatch cada flush. Solo se graba un punto si se ha movido
 * más que el UMBRAL ADAPTATIVO desde el último aceptado:
 *
 *   umbral = max(MIN_MOVEMENT_METERS, accuracy del fix)
 *
 * Adaptativo porque la magnitud del jitter escala con la incertidumbre del
 * fix: un portátil SIN GNSS se posiciona por triangulación WiFi (accuracy
 * típica 10-46-100 m) y "salta" decenas de metros entre triangulaciones sin
 * moverse — un desplazamiento menor que la propia accuracy es
 * estadísticamente indistinguible de estar parado (práctica estándar en
 * trackers: filtros por precisión/EHPE). En la tablet real con GNSS
 * (accuracy 5-15 m) el umbral efectivo queda en 10-15 m: rodando por la
 * C-25 (≥40 km/h ≈ 11 m/s) las lecturas lo superan y el track no pierde
 * densidad.
 */
const MIN_MOVEMENT_METERS = 10;

/**
 * Keepalive parado: aunque no haya movimiento, se acepta 1 punto por minuto
 * para que la "última señal" de la leyenda del mapa siga fresca (la patrulla
 * detenida sin pausar el tramo sigue dando señal de vida a sala/gestión).
 */
const STATIONARY_KEEPALIVE_MS = 60_000;

/**
 * Estado GPS "vivo" (2026-07-23, plan `2026-07-23-rv-mapa-gps-tiempo-real`
 * Task A2) — captura durable en IndexedDB (`src/offline/gpsTrackStore.ts`)
 * REEMPLAZA el parche localStorage `sr-gps-pending-*` que solo cubría el
 * final-flush fallido: ahora TODO punto aceptado se escribe en el store en
 * el momento de la lectura, antes de intentar ningún envío — nada depende
 * ya de que un flush concreto tenga éxito para sobrevivir a un cierre de
 * pestaña/app. El flush (interval + best-effort en cleanup) lee del store
 * los puntos `!synced` y los marca `synced` solo tras una resolución sin
 * throw de `addGpsPointsBatch` (éxito real O encolado offline — ver su
 * comentario: un encolado offline NO debe reintentarse desde aquí, la cola
 * ya lo reproduce al reconectar). Un throw real deja los puntos `!synced`
 * para el PRÓXIMO flush — ya no se pierden porque viven en el store.
 */
export type LivePosition = {
  lat: number;
  lng: number;
  heading?: number;
  accuracy?: number;
  timestamp: string;
};

export type LiveTrackPoint = {
  lat: number;
  lng: number;
  timestamp: string;
  /**
   * Los tres campos siguientes los consume el detector de cambio de sentido
   * (`useDirectionChangeDetection`): la precisión para descartar lecturas
   * malas, y el rumbo como señal de refuerzo. Opcionales porque el mapa —el
   * único consumidor previo de la traza— los ignora, y porque los puntos
   * rehidratados del almacén durable no traen rumbo.
   */
  accuracy?: number;
  speed?: number;
  heading?: number;
};

const MAX_LIVE_TRACK_POINTS = 2000;

interface UseSurveillanceRouteGpsTrackingOptions {
  /** Surveillance route id. If undefined, tracking is disabled. */
  routeId?: string;
  /** Current route state. Tracking is only active while IN_PROGRESS. */
  state?: string;
  /** Override for the Geolocation API — useful in tests. */
  geolocation?: Pick<
    Geolocation,
    'watchPosition' | 'clearWatch' | 'getCurrentPosition'
  >;
  /**
   * Bug reportado 2026-07-08: cualquier rol que ABRIERA un RV in-progress
   * (admin/coex/room-team en modo lectura) disparaba el prompt del
   * navegador de geolocalización, porque `isTracking` solo miraba
   * `routeId`+`state` — nunca comprobaba QUIÉN está mirando la página.
   * Solo el dispositivo de la patrulla (Tablet miembro del equipo del RV,
   * mismo gate que `canOperateTimer`) debe activar `watchPosition`.
   * Default `true` para no alterar comportamiento donde el caller no pasa
   * este flag explícitamente.
   */
  enabled?: boolean;
}

/**
 * Escucha la posición del operario mientras el recorrido está `IN_PROGRESS` y
 * mantiene el cursor y la traza en vivo que pinta el mapa.
 *
 * 🔴 **No es el dueño de la captura.** Quién debe estar grabando, el vigilante
 * que resucita el servicio nativo y el envío al backend viven en
 * `useGpsCaptureGuardian` (montado en el `TimerOverlay`, presente en toda la
 * app), porque no pueden depender de que esta pantalla esté abierta: el
 * operario la abandona constantemente durante un recorrido normal.
 *
 * Este hook deja de escuchar al desmontarse o al salir el recorrido de
 * `IN_PROGRESS`, y eso NO apaga la captura de segundo plano.
 */
export function useSurveillanceRouteGpsTracking({
  routeId,
  state,
  geolocation,
  enabled = true,
}: UseSurveillanceRouteGpsTrackingOptions) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;
  const isTablet = currentRole === Role.Tablet;
  // Estado GPS "vivo" (Task A2) — cursor y traza del tramo activo, para que
  // el mapa (Task A3) pinte al instante sin esperar al backend.
  const [livePosition, setLivePosition] = useState<LivePosition | null>(null);
  const [liveTrack, setLiveTrack] = useState<LiveTrackPoint[]>([]);
  // Retirada del listener de posición. Recibe siempre `false`: esta pantalla
  // no apaga nunca el servicio de segundo plano (eso es del guardián).
  const clearWatchRef = useRef<((stopBackgroundService: boolean) => void) | null>(
    null
  );
  const gpsBlockedRef = useRef(false);
  // Permissions API: guardamos el status para escuchar `onchange` — si el
  // usuario concede el permiso desde los ajustes del navegador, reintentamos
  // el fix y auto-cerramos el modal sin que tenga que pulsar nada.
  const permissionStatusRef = useRef<PermissionStatus | null>(null);
  // Último punto ACEPTADO (pasó el filtro de movimiento/keepalive).
  const lastAcceptedRef = useRef<{
    lat: number;
    lng: number;
    time: number;
  } | null>(null);

  const isTracking = enabled && !!routeId && state === RouteState.IN_PROGRESS;

  // Valor vivo de `isTracking` para que el cleanup del efecto pueda saber POR
  // QUÉ está limpiando (ver el comentario en el propio cleanup).
  const isTrackingRef = useRef(isTracking);
  isTrackingRef.current = isTracking;

  const dismissRef = useRef<() => void>(() => undefined);

  /**
   * Fix 2026-07-20 (QA usuario: "le doy Aceptar y no ocurre nada"). El flujo
   * anterior asumía que `getCurrentPosition` re-abre el prompt del navegador —
   * FALSO con permiso 'denied' (MDN/Chrome: una vez bloqueado, el navegador
   * NO vuelve a preguntar; hay que concederlo en ajustes). Flujo correcto:
   *  1. `navigator.permissions.query({name:'geolocation'})`:
   *     - 'denied'  → devolver 'denied' (el modal muestra instrucciones) y
   *       escuchar `onchange` → al conceder desde ajustes, reintento + cierre.
   *     - 'prompt'/'granted'/sin API → intentar `getCurrentPosition`.
   *  2. Éxito del fix → cerrar el modal AQUÍ MISMO (antes solo cerraba cuando
   *     el watcher emitía — otra razón del "no ocurre nada").
   * Devuelve el resultado para que el modal dé feedback ('denied' → ayuda).
   */
  const handleRequestGps = useCallback(async (): Promise<
    'granted' | 'denied' | 'no-fix'
  > => {
    const source = getPositionSource(geolocation);
    if (!source) return 'no-fix';

    const attemptFix = (): Promise<'granted' | 'denied' | 'no-fix'> =>
      new Promise((resolve) => {
        source.getCurrent(
          () => {
            dismissRef.current();
            resolve('granted');
          },
          (err) => resolve(err.code === 1 ? 'denied' : 'no-fix')
        );
      });

    try {
      if (
        // En la app NO se consulta: dentro del WebView esa respuesta no es una
        // decisión del usuario (ver `permissionApiDecides`), y tomarla por una
        // negativa devolvería 'denied' sin llegar a llamar al sistema. El
        // operario pulsaría "Aceptar" y no ocurriría nada — exactamente el
        // fallo que este flujo arregló en su día para el navegador.
        permissionApiDecides() &&
        typeof navigator !== 'undefined' &&
        navigator.permissions?.query &&
        !permissionStatusRef.current
      ) {
        const status = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        });
        permissionStatusRef.current = status;
        status.onchange = () => {
          if (status.state === 'granted') void attemptFix();
        };
      }
    } catch {
      // Permissions API no disponible (Safari antiguo) — seguimos con el intento directo.
    }

    if (permissionStatusRef.current?.state === 'denied') return 'denied';
    return attemptFix();
  }, [geolocation]);

  const showGpsRequired = useCallback(
    (errorCode?: number) => {
      // V4 P14 FR-14.1: "Solo para rol Tablet" — los demás roles (Coex, RoomTeam,
      // SuperAdmin) pueden estar viendo la página del RV en modo lectura sin
      // operar el timer; para ellos no tiene sentido el modal bloqueante.
      if (!isTablet) return;
      // Fix 2026-07-20 (QA usuario: "a veces sale y se queda pillado"): el
      // TIMEOUT (code 3) es pérdida de señal TRANSITORIA — un portátil por
      // triangulación WiFi o la tablet en un túnel lo dan esporádicamente con
      // el permiso concedido. El watcher sigue reintentando solo; bloquear
      // toda la app (incluido "Finalizar tramo") por un timeout dejaba al
      // usuario en una cárcel sin salida. El modal queda para DENIED (1,
      // permiso revocado) y POSITION_UNAVAILABLE (2, ubicación del SO
      // apagada) — el caso real de FR-14 "reactivar el GPS".
      if (errorCode === 3) return;
      if (gpsBlockedRef.current) return;
      gpsBlockedRef.current = true;
      dispatch(
        openModal(modalIds.GPS_REQUIRED, {
          onRequestGps: handleRequestGps,
        })
      );
    },
    [dispatch, handleRequestGps, isTablet]
  );

  const dismissGpsRequired = useCallback(() => {
    if (!gpsBlockedRef.current) return;
    gpsBlockedRef.current = false;
    dispatch(closeModal());
  }, [dispatch]);
  dismissRef.current = dismissGpsRequired;

  useEffect(() => {
    if (!isTracking) return;

    // Fuente de posición: el plugin nativo dentro de la app Android (captura
    // con la pantalla apagada), `navigator.geolocation` en el navegador, o el
    // mock que inyectan los tests. El resto del hook no distingue cuál es.
    const source = getPositionSource(geolocation);
    if (!source) {
      Sentry.captureMessage('[SR-GPS] Geolocation API not available', {
        level: 'warning',
        tags: {
          feature: 'surveillance-route',
          hook: 'useSurveillanceRouteGpsTracking',
        },
      });
      return;
    }

    // (Re)arranque de tracking para este routeId (nuevo tramo, o resume tras
    // pausa) — el cursor/traza vivos empiezan de cero; el tramo anterior ya
    // quedó reflejado en `gpsPoints` del backend, y el gate del mapa (Task
    // A3) solo pinta la traza en vivo del tramo ACTIVO actual.
    setLiveTrack([]);
    setLivePosition(null);

    // 🔴 …pero SÍ se rehidrata desde el almacén durable. El supuesto original
    // —"el tramo anterior ya quedó reflejado en `gpsPoints` del backend"— solo
    // vale ONLINE. Sin cobertura los puntos siguen en IndexedDB esperando el
    // flush, así que recargar la tablet a media ruta borraba de la vista por
    // dónde había ido la patrulla: el dato estaba a salvo, la representación
    // no — y el operario no puede distinguir "no se ve" de "no se grabó".
    //
    // Se siembra la traza ENTERA del recorrido; el gate del mapa ya la recorta
    // al tramo activo por `startDate`, así que aquí no hace falta saber cuál es.
    let seedCancelled = false;
    void getLocalTrack(routeId)
      .then((storedPoints) => {
        if (seedCancelled || storedPoints.length === 0) return;
        // Merge y no reemplazo: entre el montaje y esta lectura asíncrona el
        // watcher ya puede haber capturado posiciones, que son las buenas.
        setLiveTrack((previous) =>
          mergeStoredLiveTrack(previous, storedPoints, MAX_LIVE_TRACK_POINTS)
        );
      })
      .catch(() => {
        // Best-effort: sin rehidratación la traza arranca vacía, que es el
        // comportamiento anterior — nunca un error para el operario.
      });

    // Success handler compartido por `watchPosition` (lecturas continuas) y
    // el refresco puntual de `visibilitychange`→visible (Task D2, plan
    // Bloque D caso 7 "segundo plano") — extraído para que un refresco
    // manual (`getCurrentPosition`) pase por el MISMO filtro de
    // movimiento/keepalive y la MISMA escritura durable que una lectura
    // normal del watcher, sin duplicar lógica ni poder desincronizarse.
    const handlePositionUpdate = (position: GeolocationPosition): void => {
      // V4 P14 FR-14.3 auto-dismiss: GPS válido → cerrar modal si estaba abierto.
      dismissGpsRequired();

      // Filtro de movimiento: descartar el jitter de un dispositivo
      // quieto (salvo keepalive 1/min para mantener la "última señal").
      // Umbral ADAPTATIVO a la precisión del fix — ver doc de
      // MIN_MOVEMENT_METERS arriba.
      const { latitude, longitude, accuracy } = position.coords;
      const readingTime = position.timestamp || Date.now();
      const last = lastAcceptedRef.current;
      const movementThreshold = Math.max(MIN_MOVEMENT_METERS, accuracy ?? 0);
      const movedEnough =
        !last ||
        haversineMeters(last.lat, last.lng, latitude, longitude) >=
          movementThreshold;
      const keepaliveDue =
        !!last && readingTime - last.time >= STATIONARY_KEEPALIVE_MS;
      if (!movedEnough && !keepaliveDue) return;
      lastAcceptedRef.current = {
        lat: latitude,
        lng: longitude,
        time: readingTime,
      };

      const isoTimestamp = new Date(position.timestamp).toISOString();
      const localPoint: LocalGpsPoint = {
        latitude,
        longitude,
        timestamp: isoTimestamp,
        speed: position.coords.speed ?? undefined,
        accuracy: position.coords.accuracy ?? undefined,
        // El detector de cambio de sentido se alimenta ahora del almacén, no
        // de `liveTrack`: si el rumbo no viaja aquí, su señal S3 desaparece.
        heading: position.coords.heading ?? undefined,
      };
      // Escritura durable — fire-and-forget (IndexedDB es async y no debe
      // bloquear el callback de watchPosition). El flush (interval/cleanup)
      // es quien lee esto y lo envía al backend.
      if (routeId) {
        appendGpsPoints(routeId, [localPoint]).catch((err) => {
          console.warn(
            '[SR-GPS] failed to persist point to local track store',
            err
          );
        });
      }

      // Estado vivo — el mapa (Task A3) pinta el cursor/traza al instante,
      // sin depender del flush ni de un refetch del backend.
      setLivePosition({
        lat: latitude,
        lng: longitude,
        heading: position.coords.heading ?? undefined,
        accuracy,
        timestamp: isoTimestamp,
      });
      setLiveTrack((prev) =>
        [
          ...prev,
          {
            lat: latitude,
            lng: longitude,
            timestamp: isoTimestamp,
            accuracy,
            speed: position.coords.speed ?? undefined,
            heading: position.coords.heading ?? undefined,
          },
        ].slice(-MAX_LIVE_TRACK_POINTS)
      );
    };

    const handleWatchError = (error: { code: number; message: string }): void => {
      // V4 P14 FR-14.1: permiso revocado o degradado → modal bloqueante.
      Sentry.captureMessage(`[SR-GPS] watchPosition error: ${error.message}`, {
        level: 'warning',
        tags: {
          feature: 'surveillance-route',
          hook: 'useSurveillanceRouteGpsTracking',
          code: String(error.code),
        },
      });
      showGpsRequired(error.code);
    };

    // Drenaje del buffer nativo: en la app, el foreground service ha podido
    // estar capturando durante horas con el WebView congelado o muerto. Esos
    // puntos existen en SQLite y hay que incorporarlos ANTES de nada, para que
    // el flush los suba junto con los nuevos. En web es un no-op.
    const drainNative = (): void => {
      if (!isNativeApp() || !routeId) return;
      drainNativeGpsBuffer(routeId)
        .then((count) => {
          if (count > 0) {
            console.info(`[SR-GPS] ${count} puntos recuperados del buffer nativo`);
          }
        })
        .catch((err) => {
          // No se pierde nada: `drain` no borra, así que el siguiente intento
          // vuelve a leer los mismos puntos.
          console.warn('[SR-GPS] no se pudo drenar el buffer nativo', err);
        });
    };

    // `watch` es asíncrono porque en nativo hay que levantar el foreground
    // service. `cancelled` cubre la carrera en la que el efecto se limpia
    // antes de que la promesa resuelva (tramo cerrado muy rápido, StrictMode).
    let cancelled = false;
    void (async () => {
      drainNative();
      try {
        const clear = await source.watch(handlePositionUpdate, handleWatchError, {
          routeId,
        });
        if (cancelled) {
          clear(!isTrackingRef.current);
          return;
        }
        clearWatchRef.current = clear;
      } catch (err) {
        handleWatchError({
          code: 2,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    // 🔴 Aquí YA NO hay watchdog, ni envío al backend, ni escucha de `resume`.
    // Todo eso vive ahora en `useGpsCaptureGuardian`, montado en el
    // `TimerOverlay` — o sea, en toda la app. El motivo está medido: esta
    // pantalla se desmonta en cuanto el operario navega a cualquier otro sitio
    // (la pestaña de al lado ya cuenta), y con ella se iba el único que sabía
    // resucitar el servicio nativo. El 12-ago-2026 una tablet estuvo 47 horas
    // sin grabar por eso, con la app en uso y el coche rodando.
    //
    // Lo que queda aquí es lo que SOLO tiene sentido con la pantalla delante:
    // escuchar posiciones para pintar el cursor y la traza en vivo.

    // `visibilitychange`:
    //  - visible → `watchPosition` puede tardar unos segundos en volver a
    //    emitir tras el letargo del navegador (o el SO llegó a congelar el
    //    GPS mientras la pestaña estaba oculta pese al Wake Lock — p.ej. si
    //    el usuario cambió de app en vez de solo bloquear pantalla); un
    //    `getCurrentPosition` de una sola vez refresca `livePosition` al
    //    instante por el MISMO camino que una lectura normal del watcher
    //    (`handlePositionUpdate`), en vez de dejar el cursor con la última
    //    posición conocida antes de esconderse.
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        // En la app nativa esto es lo que recupera la traza tras un rato en
        // segundo plano: el servicio siguió grabando en SQLite y aquí es donde
        // esos puntos entran en IndexedDB y aparecen en el mapa.
        drainNative();
        source.getCurrent(handlePositionUpdate, () => {
          /* best-effort — si falla (timeout/denied), el watcher normal
             sigue activo y emitirá en cuanto tenga un fix propio */
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      // La lectura del almacén es asíncrona: sin esto, un desmontaje rápido
      // (cambiar de pantalla nada más entrar) sembraría la traza de un
      // recorrido que ya no se está mirando.
      seedCancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // 🔴 Esta pantalla NUNCA apaga el foreground service. Quita su listener y
      // nada más.
      //
      // Antes decidía aquí si apagarlo mirando si el tramo seguía activo, y esa
      // decisión estaba tomada en el sitio equivocado: cambiar de pestaña o
      // abrir OTRO recorrido —incluso uno terminado hace un mes— pasaba por
      // este cleanup y podía cortarle la traza a la patrulla que estaba
      // grabando. Quién debe capturar es una condición de negocio ("¿hay un
      // recorrido en curso de este dispositivo?"), no una propiedad de la
      // pantalla que se esté mirando, así que ahora lo decide el guardián
      // global, que sí ve los recorridos activos.
      if (clearWatchRef.current) {
        clearWatchRef.current(false);
        clearWatchRef.current = null;
      }
      // Último drenaje: acerca al mapa lo que el servicio grabó entre el
      // penúltimo ciclo y ahora. El envío al backend lo sigue haciendo el
      // guardián, esté esta pantalla montada o no.
      drainNative();
      // Al salir del tracking (tramo cerrado / unmount) cerramos el modal GPS
      // si quedó visible — ya no es bloqueante fuera de tramo activo.
      dismissGpsRequired();
      if (permissionStatusRef.current) {
        permissionStatusRef.current.onchange = null;
        permissionStatusRef.current = null;
      }
      // Tramo cerrado / pausa / unmount: el cursor y la traza en vivo dejan
      // de representar un tramo activo — refuerza el gate del mapa (Task
      // A3), que ya ignora la traza en vivo sin tramo activo.
      setLivePosition(null);
      setLiveTrack([]);
      // Reset del filtro de movimiento: el siguiente tramo/tracking graba
      // SIEMPRE su primer punto (last=null → movedEnough=true).
      lastAcceptedRef.current = null;
    };
  }, [
    isTracking,
    routeId,
    geolocation,
    showGpsRequired,
    dismissGpsRequired,
  ]);

  return { livePosition, liveTrack };
}

export default useSurveillanceRouteGpsTracking;
